<template>
  <footer class="footer tech-accent">
    <div class="container">
      <!-- 非用户中心页面显示完整页脚 -->
      <div v-if="!isUserCenter">
        <div class="footer-links">
          <div class="footer-links-column">
            <h4 class="footer-links-title">{{ $t('Footer.introduction') }}</h4>
            <ul class="footer-links-list">
              <li><router-link to="/introduction#what-is-quant">{{ $t('Footer.quantTrading') }}</router-link></li>
              <li><router-link to="/introduction#quant-advantages">{{ $t('Footer.quantAdvantages') }}</router-link></li>
              <li><router-link to="/introduction#how-to-start">{{ $t('Footer.howToStart') }}</router-link></li>
            </ul>
          </div>
          
          <div class="footer-links-column">
            <h4 class="footer-links-title">{{ $t('Footer.about') }}</h4>
            <ul class="footer-links-list">
              <li><router-link to="/about#company">{{ $t('Footer.company') }}</router-link></li>
              <li><router-link to="/about#values">{{ $t('Footer.values') }}</router-link></li>
            </ul>
          </div>
          
          <div class="footer-links-column">
            <h4 class="footer-links-title">{{ $t('Footer.help') }}</h4>
            <ul class="footer-links-list">
              <li><router-link to="/help/fund-security">{{ $t('Footer.fundSecurity') }}</router-link></li>
              <li><router-link to="/help/profit-model">{{ $t('Footer.profitModel') }}</router-link></li>
              <li><router-link to="/help/operation-process">{{ $t('Footer.operationProcess') }}</router-link></li>
              <li><router-link to="/help/agent-rules">{{ $t('Footer.agentRules') }}</router-link></li>
            </ul>
          </div>
          
          <div class="footer-links-column">
            <h4 class="footer-links-title">{{ $t('Footer.contact') }}</h4>
            <div class="footer-contact-links">
              <a
                :href="getContactLink('whatsapp')"
                class="contact-link whatsapp"
                @click="handleContactClick('whatsapp', $event)"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i class="fab fa-whatsapp"></i>
                <span>WhatsApp: {{ contactInfo.whatsapp }}</span>
              </a>
              <a
                :href="getContactLink('twitter')"
                class="contact-link twitter"
                @click="handleContactClick('twitter', $event)"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i class="fab fa-twitter"></i>
                <span>Twitter: {{ contactInfo.twitter }}</span>
              </a>
              <a
                :href="getContactLink('telegram')"
                class="contact-link telegram"
                @click="handleContactClick('telegram', $event)"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i class="fab fa-telegram-plane"></i>
                <span>Telegram: {{ contactInfo.telegram }}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 所有页面都显示页脚底部版权信息 -->
      <div class="footer-bottom">
        <div class="footer-copyright">
          {{ $t('Footer.copyright', { year: currentYear }) }} 
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import { computed } from 'vue'
import { useRoute } from 'vue-router'

export default {
  name: 'Footer',
  setup() {
    const route = useRoute()

    // 获取当前年份
    const currentYear = computed(() => new Date().getFullYear())

    // 从环境变量获取联系方式信息
    const contactInfo = {
      whatsapp: import.meta.env.VITE_CONTACT_WHATSAPP || '+601172200814',
      twitter: import.meta.env.VITE_CONTACT_TWITTER || '@Firmhedg',
      telegram: import.meta.env.VITE_CONTACT_TELEGRAM || '@Firmhedg'
    }

    // 判断是否在用户中心
    const isUserCenter = computed(() => {
      return route.path.startsWith('/user') || route.path.startsWith('/zjfl')
    })

    // 判断是否在首页
    const isHomePage = computed(() => {
      return route.path === '/' || route.path === '/home'
    })

    // 检测用户平台
    const getUserAgent = () => {
      return navigator.userAgent.toLowerCase()
    }

    const isMobile = () => {
      const userAgent = getUserAgent()
      return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent)
    }

    const isAndroid = () => {
      return getUserAgent().includes('android')
    }

    const isIOS = () => {
      const userAgent = getUserAgent()
      return /iphone|ipad|ipod/i.test(userAgent)
    }

    // 生成联系方式链接
    const getContactLink = (platform) => {
      switch (platform) {
        case 'whatsapp':
          const whatsappNumber = contactInfo.whatsapp.replace(/[^\d]/g, '') // 移除非数字字符
          if (isMobile()) {
            return `whatsapp://send?phone=${whatsappNumber}`
          }
          return `https://wa.me/${whatsappNumber}`

        case 'twitter':
          const twitterHandle = contactInfo.twitter.replace('@', '') // 移除@符号
          if (isMobile()) {
            if (isIOS()) {
              return `twitter://user?screen_name=${twitterHandle}`
            } else if (isAndroid()) {
              return `intent://twitter.com/${twitterHandle}#Intent;package=com.twitter.android;scheme=https;end`
            }
          }
          return `https://twitter.com/${twitterHandle}`

        case 'telegram':
          const telegramHandle = contactInfo.telegram.replace('@', '') // 移除@符号
          if (isMobile()) {
            return `tg://resolve?domain=${telegramHandle}`
          }
          return `https://t.me/${telegramHandle}`

        default:
          return '#'
      }
    }

    // 处理联系方式点击
    const handleContactClick = (platform, event) => {
      const link = getContactLink(platform)

      // 如果是移动端的应用链接，尝试打开应用
      if (isMobile() && (link.startsWith('whatsapp://') || link.startsWith('twitter://') || link.startsWith('tg://') || link.startsWith('intent://'))) {
        event.preventDefault()

        // 尝试打开应用
        window.location.href = link

        // 如果应用未安装，延迟后跳转到网页版
        setTimeout(() => {
          switch (platform) {
            case 'whatsapp':
              const whatsappNumber = contactInfo.whatsapp.replace(/[^\d]/g, '')
              window.open(`https://wa.me/${whatsappNumber}`, '_blank', 'noopener,noreferrer')
              break
            case 'twitter':
              const twitterHandle = contactInfo.twitter.replace('@', '')
              window.open(`https://twitter.com/${twitterHandle}`, '_blank', 'noopener,noreferrer')
              break
            case 'telegram':
              const telegramHandle = contactInfo.telegram.replace('@', '')
              window.open(`https://t.me/${telegramHandle}`, '_blank', 'noopener,noreferrer')
              break
          }
        }, 1000)
      } else {
        // 桌面端直接在新标签页打开（由于已设置target="_blank"，这里不需要额外处理）
        // 但我们可以添加一些额外的处理逻辑
        console.log(`Opening ${platform} contact link: ${link}`)
      }
    }

    return {
      currentYear,
      isUserCenter,
      isHomePage,
      contactInfo,
      getContactLink,
      handleContactClick
    }
  }
}
</script>

<style scoped>
@import '@/styles/components/footer.css';
</style>
